import { useEffect, useMemo, useRef, useState } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { fetchCustomers } from '../../../services/customers/customersApi';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Chọn khách để gắn vào room (dùng trong Form.Item: value là customer id, xoá chọn = gỡ khách).
 * `selected` là khách đang gắn ({ id, name }) để hiện tên khi danh sách tìm chưa có khách đó.
 */
const CustomerSelect = ({ value, onChange, selected, disabled }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef(null);

  const search = async keyword => {
    setIsLoading(true);
    try {
      const { customers: list } = await fetchCustomers({
        search: keyword || undefined,
        limit: 20,
      });
      setCustomers(list);
    } catch {
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    search('');
    return () => clearTimeout(timerRef.current);
  }, []);

  const options = useMemo(() => {
    const list = customers.map(customer => ({
      value: customer.id,
      label: customer.is_confirmed_contract
        ? `${customer.name} (${t('Confirmed')})`
        : customer.name,
    }));
    if (selected?.id && !list.some(option => option.value === selected.id)) {
      list.unshift({ value: selected.id, label: selected.name });
    }
    return list;
  }, [customers, selected, t]);

  return (
    <Select
      size="large"
      showSearch
      allowClear
      filterOption={false}
      loading={isLoading}
      disabled={disabled}
      value={value}
      options={options}
      placeholder={t('Select customer')}
      onChange={next => onChange?.(next ?? null)}
      onSearch={keyword => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(
          () => search(keyword),
          SEARCH_DEBOUNCE_MS
        );
      }}
    />
  );
};

export default CustomerSelect;
